export const convertDate = (
  date: string | Date | undefined
): string | undefined => {
  if (!date) return;

  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

export const convertSampleCollectionDate = (
  sampleCollectionDate: string
): string => {
  const [y, m, d] = sampleCollectionDate.split("T")[0].split("-");
  return `${m}/${d}/${y}`;
};

export const formatDateToISO = ({
  year,
  month,
  day,
}: {
  year: string | number;
  month: string | number;
  day: string | number;
}): string => {
  const paddedMonth = String(month).padStart(2, "0");
  const paddedDay = String(day).padStart(2, "0");
  return `${year}-${paddedMonth}-${paddedDay}`;
};

export const atUtcMidnight = (d: Date) =>
  new Date(new Date(d).setUTCHours(0, 0, 0, 0));

export const addUtcDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + n);
  return x;
};

export const parseDateOnly = (input: string | Date): Date => {
  // Always return a normalized "today" date if parsing fails
  const fallbackDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };

  // If input is already a Date -> normalize to a date-only value
  if (input instanceof Date) {
    if (isNaN(input.getTime())) return fallbackDate();
    return new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }

  const raw = input.trim();

  // --- 1. ISO with time: 2003-01-01T00:00:00Z
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return fallbackDate();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  // --- 2. YYYY-MM-DD or YYYY/MM/DD
  if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split(/[-/]/).map(Number);
    return new Date(y, m - 1, d);
  }

  // --- 3. DD.MM.YYYY
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(raw)) {
    const [d, m, y] = raw.split(".").map(Number);
    return new Date(y, m - 1, d);
  }

  // --- 4. MM/DD/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [m, d, y] = raw.split("/").map(Number);
    return new Date(y, m - 1, d);
  }

  // --- 5. Last fallback: try parsing generically, normalize result
  const parsed = new Date(raw);
  if (!isNaN(parsed.getTime())) {
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }

  // If nothing matched — return today
  return fallbackDate();
};

export const formatLocalDate = (date?: Date | undefined): string => {
  if (!date || isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};
